import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { isAfter, isBefore, isSameDay } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { z } from 'zod';

import { useLatestBedrijf } from '@/api/queries/bedrijf';
import { Disclaimer } from '@/components/Disclaimer/Disclaimer';
import { Divider } from '@/components/Divider/Divider';
import { DocumentTitle } from '@/components/DocumentTitle';
import { FeedbackMessage } from '@/components/FeedbackMessage';
import { useOpenModal } from '@/components/Modal';
import { Nav } from '@/components/Nav/Nav';
import { Navbar } from '@/components/Navbar/Navbar';
import { ObjectHoofdstuk } from '@/components/Objects/ObjectHoofdstuk';
import { ObjectParagraaf } from '@/components/Objects/ObjectParagraaf';
import { ObjectSubparagraaf } from '@/components/Objects/ObjectSubparagraaf';
import { ObjectTransparantSubparagraaf } from '@/components/Objects/ObjectTransparentSubparagraaf';
import { TextButton } from '@/components/TextButton/TextButton';
import { TooltipWhenOverflowing } from '@/components/TooltipWhenOverflowing/TooltipWhenOverflowing';
import { Warning } from '@/components/Warning';
import { eStrings } from '@/constants/strings';
import { RegularList } from '@/icons/components';
import { JumpToParagraaf } from '@/modals/JumpToParagraaf';
import { DateUtils } from '@/utils/dateUtils';
import { useOffsetTop } from '@/utils/hooks/useOffsetTop';

import { Body } from '../-components/Body';
import { ChapterNavigation } from '../-components/ChapterNavigation';
import { ViewerVoorschrift } from '../-components/ViewerVoorschrift';
import { VigerendHeader } from './-components/VigerendHeader';
import { ensureQueryDataMaxBeginGeldigheid } from './-utils/useMaxBeginGeldigheid';
import { ensureQueryDataMinBeginGeldigheid } from './-utils/useMinBeginGeldigheid';
import {
  HoofdstukLineage,
  ParagraafLineage,
  VigerendData,
  useVigerendData,
} from './-utils/useVigerendData';

/**
 * Search schema
 */
const searchSchema = z.object({
  datum: z.string().date().catch(DateUtils.formatForInput(new Date())),
  hoofdstukLineageId: z.string().optional(),
});

/**
 * Route
 */
export const Route = createFileRoute('/$bedrijfLineageId/viewer/vigerend/')({
  validateSearch: searchSchema.parse,
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({
    params: { bedrijfLineageId },
    context: { queryClient },
    deps: { search },
  }) => {
    const bedrijf = await queryClient.ensureQueryData(
      useLatestBedrijf.getOptions(bedrijfLineageId)
    );

    const bedrijfId = bedrijf.objects?.[0]?.ID ?? '';
    const searchDate = new Date(search.datum);

    const [minBeginGeldigheid, maxBeginGeldigheid] = await Promise.all([
      ensureQueryDataMinBeginGeldigheid({ bedrijfId, queryClient }),
      ensureQueryDataMaxBeginGeldigheid({ bedrijfId, queryClient }),
    ]);

    /**
     * If the date is before the min begin geldigheid date, navigate to the min begin geldigheid date
     */
    if (isBefore(searchDate, minBeginGeldigheid)) {
      FeedbackMessage({
        type: 'info',
        description:
          'De opgegeven datum kan niet worden gebruikt, de datum is aangepast naar een beschikbaar datum.',
      });

      throw redirect({
        to: '/$bedrijfLineageId/viewer/vigerend',
        search: { datum: DateUtils.formatForUrl(minBeginGeldigheid) },
        params: { bedrijfLineageId },
      });
    }

    /**
     * If the date is after the max begin geldigheid date, navigate to the max begin geldigheid date
     */
    if (
      isAfter(searchDate, maxBeginGeldigheid) &&
      !isSameDay(searchDate, maxBeginGeldigheid)
    ) {
      FeedbackMessage({
        type: 'info',
        description:
          'De opgegeven datum kan niet worden gebruikt, de datum is aangepast naar een beschikbaar datum.',
      });

      throw redirect({
        to: '/$bedrijfLineageId/viewer/vigerend',
        search: { datum: DateUtils.formatForUrl(maxBeginGeldigheid) },
        params: { bedrijfLineageId },
      });
    }

    await queryClient.ensureQueryData(
      useVigerendData.getOptions({
        date: new Date(search.datum),
        bedrijfLineageId,
      })
    );
  },
  component: VigerendComponent,
});

/**
 * Route component
 */
function VigerendComponent() {
  const { bedrijfLineageId } = Route.useParams();
  const { datum, hoofdstukLineageId } = Route.useSearch();
  const datumObject = new Date(datum);

  const bedrijf = useLatestBedrijf(bedrijfLineageId);

  const grondslagen = useVigerendData({
    date: datumObject,
    bedrijfLineageId,
  });

  /**
   * Get current, next and previous hoofdstuk
   */
  let currentHoofdstuk: HoofdstukLineage | undefined = undefined;
  let nextHoofdstuk: HoofdstukLineage | undefined = undefined;
  let previousHoofdstuk: HoofdstukLineage | undefined = undefined;

  if (grondslagen.data?.length) {
    const hoofdstukken = grondslagen.data.flatMap(
      ({ hoofdstukken }) => hoofdstukken
    );

    currentHoofdstuk = hoofdstukLineageId
      ? hoofdstukken.find(({ lineageId }) => lineageId === hoofdstukLineageId)
      : hoofdstukken[0];

    if (currentHoofdstuk) {
      const currentIndex = hoofdstukken.indexOf(currentHoofdstuk);
      nextHoofdstuk = hoofdstukken[currentIndex + 1];
      previousHoofdstuk = hoofdstukken[currentIndex - 1];
    }
  }

  if (!bedrijf.data || !grondslagen.data) return null;

  return (
    <>
      <DocumentTitle
        sections={[
          eStrings.LBL_DIGI_V,
          bedrijf.data.Naam ?? '',
          'Vigerende situatie',
        ]}
      />

      <Navbar />

      <VigerendHeader bedrijf={bedrijf.data} />

      <Body>
        <Body.Sidebar ariaLabel="Vigerende situatie navigatie">
          <Sidebar
            grondslagen={grondslagen.data}
            bedrijfLineageId={bedrijfLineageId}
            datum={datum}
          />
        </Body.Sidebar>

        <Body.Content>
          <PastWarning date={datumObject} />

          {currentHoofdstuk && (
            <Objects date={datumObject} hoofdstukLineage={currentHoofdstuk} />
          )}

          <ChapterNavigation
            previous={
              previousHoofdstuk?.hoofdstukken[0]?.ID && (
                <ChapterNavigation.Previous
                  subtitle="Vorig hoofdstuk"
                  grondslag={previousHoofdstuk.grondslag}
                  nummer={previousHoofdstuk.hoofdstukken[0].Nummer}
                  htmlTitle={previousHoofdstuk.hoofdstukken[0].Titel}
                  to="/$bedrijfLineageId/viewer/vigerend"
                  params={{
                    bedrijfLineageId,
                  }}
                  search={{
                    hoofdstukLineageId: previousHoofdstuk.lineageId,
                    datum,
                  }}
                />
              )
            }
            next={
              nextHoofdstuk?.hoofdstukken[0]?.ID && (
                <ChapterNavigation.Next
                  grondslag={nextHoofdstuk.grondslag}
                  subtitle="Volgend hoofdstuk"
                  nummer={nextHoofdstuk.hoofdstukken[0].Nummer}
                  htmlTitle={nextHoofdstuk.hoofdstukken[0].Titel}
                  to="/$bedrijfLineageId/viewer/vigerend"
                  params={{
                    bedrijfLineageId,
                  }}
                  search={{
                    hoofdstukLineageId: nextHoofdstuk.lineageId,
                    datum,
                  }}
                />
              )
            }
          />

          <Disclaimer className="mt-2 px-6" />
        </Body.Content>
      </Body>
    </>
  );
}

const Sidebar = ({
  grondslagen,
  bedrijfLineageId,
  datum,
}: {
  grondslagen: VigerendData;
  bedrijfLineageId: string;
  datum: string;
}) => {
  return (
    <Nav withAccordion={false} aria-label="Inhoudsopgave">
      {grondslagen.map((grondslag, index) => (
        <Fragment key={`vigerend-grondslag-${grondslag.data.id}`}>
          <Nav.Header icon={grondslag.data.icon}>
            {grondslag.data.label}
          </Nav.Header>

          <Nav.List>
            {grondslag.hoofdstukken.map((vigerendHoofdstuk) => {
              const hoofdstuk = vigerendHoofdstuk.hoofdstukken[0];

              if (!hoofdstuk) return null;

              return (
                <Nav.ListItem key={`vigerend-hoofdstuk-${hoofdstuk.ID}`}>
                  <Nav.Button asChild>
                    <Link
                      to="/$bedrijfLineageId/viewer/vigerend"
                      params={{
                        bedrijfLineageId,
                      }}
                      search={{
                        hoofdstukLineageId: hoofdstuk.Lineage,
                        datum,
                      }}
                    >
                      <TooltipWhenOverflowing
                        description={hoofdstuk.Titel}
                        asHTML
                        side="top"
                      >
                        <span
                          dangerouslySetInnerHTML={{
                            __html: `<span>${hoofdstuk.Nummer}</span> ${hoofdstuk.Titel}`,
                          }}
                        />
                      </TooltipWhenOverflowing>
                    </Link>
                  </Nav.Button>
                </Nav.ListItem>
              );
            })}
          </Nav.List>

          {grondslagen.length - 1 !== index && <Divider className="my-5" />}
        </Fragment>
      ))}
    </Nav>
  );
};

const Objects = ({
  date,
  hoofdstukLineage,
}: {
  date: Date;
  hoofdstukLineage: HoofdstukLineage;
}) => {
  const hoofdstukToRender = hoofdstukLineage.hoofdstukken[0]!;
  const { ref: listRef, offsetTop } = useOffsetTop();

  /**
   * Virtualizer
   */
  const virtualizer = useWindowVirtualizer({
    count: hoofdstukLineage.paragraafLineages.length ?? 0,
    estimateSize: () => 1000,
    overscan: 2,
    enabled: !!hoofdstukLineage.paragraafLineages.length,
    scrollMargin: offsetTop,
    paddingEnd: 200,
    initialOffset: 0, // Fixes an issue where the page starts at the middle of the container
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  const onJumpToParagraaf = useCallback(
    (index: number) => virtualizer.scrollToIndex(index, { align: 'start' }),
    [virtualizer]
  );

  return (
    <>
      <ContentHeader
        date={date}
        hoofdstukNummer={hoofdstukToRender.Nummer}
        paragraafLineages={hoofdstukLineage.paragraafLineages}
        onJumpToParagraaf={onJumpToParagraaf}
      />

      {totalSize === 0 ? (
        <ObjectHoofdstuk
          className="mt-2"
          number={`${hoofdstukToRender.Nummer}`}
          title={hoofdstukToRender.Titel}
          titleAs="h1"
        />
      ) : (
        <div
          className="relative mt-2 w-full"
          ref={listRef}
          style={{
            height: totalSize,
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${(virtualItems[0]?.start ?? 0) - offsetTop}px)`,
            }}
            className="space-y-2 pt-2"
          >
            <ObjectHoofdstuk
              number={`${hoofdstukToRender.Nummer}`}
              title={hoofdstukToRender.Titel}
              titleAs="h1"
            />

            {virtualItems.map((item) => {
            const paragraafLineage =
              hoofdstukLineage.paragraafLineages[item.index];
            const paragraaf = paragraafLineage?.paragrafen[0];

            if (!paragraaf) return null;

            return (
              <ObjectParagraaf
                key={item.key}
                ref={virtualizer.measureElement}
                data-index={item.index}
                number={`${hoofdstukLineage.hoofdstukken[0]?.Nummer}.${paragraaf.Nummer}`}
                title={paragraaf.Titel}
                titleAs="h2"
              >
                <div className="space-y-2 py-2">
                  {paragraafLineage.subparagraafLineages.map(
                    (vigerendSubparagraaf) => {
                      const subparagraaf =
                        vigerendSubparagraaf.subparagrafen[0];

                      if (!subparagraaf) return null;

                      const Component = subparagraaf.Transparent
                        ? ObjectTransparantSubparagraaf
                        : ObjectSubparagraaf;

                      return (
                        <Component
                          titleAs="h3"
                          key={`vigerend-subparagraaf-${subparagraaf.ID}`}
                          title={subparagraaf.Titel}
                        >
                          <div className="space-y-1 py-2">
                            {vigerendSubparagraaf.voorschriften.map(
                              (voorschrift) => (
                                <ViewerVoorschrift
                                  key={`vigerend-voorschrift-${voorschrift.data.ID}`}
                                  voorschrift={voorschrift.data}
                                  VSPB={voorschrift.VSPB}
                                  hoofdstuk={hoofdstukToRender}
                                  paragraafNummer={paragraaf.Nummer}
                                  paragraafId={paragraaf.ID}
                                  mode="vigerend"
                                  testId={`vigerend-voorschrift-${voorschrift.VSPB.ID}`}
                                />
                              )
                            )}
                          </div>
                        </Component>
                      );
                    }
                  )}
                </div>
              </ObjectParagraaf>
            );
            })}
          </div>
        </div>
      )}
    </>
  );
};

const PastWarning = ({ date }: { date: Date }) => {
  const isToday = isSameDay(date, new Date().setHours(0, 0, 0, 0));

  if (isToday) return;

  return (
    <Warning size="large" variant="info">
      <Warning.Title asChild>
        <h3>Vigerende situatie</h3>
      </Warning.Title>

      <Warning.Description>
        Het getoonde overzicht is van {DateUtils.formatReadable(date)}, ga naar{' '}
        <TextButton size="small" asChild>
          <Link
            to={Route.fullPath}
            from={Route.fullPath}
            search={{ datum: DateUtils.formatForUrl(new Date()) }}
          >
            vandaag
          </Link>
        </TextButton>{' '}
        om de actuele vigerende situatie te bekijken.
      </Warning.Description>
    </Warning>
  );
};

const ContentHeader = ({
  date,
  hoofdstukNummer,
  paragraafLineages,
  onJumpToParagraaf,
}: {
  date: Date;
  hoofdstukNummer: number;
  paragraafLineages: ParagraafLineage[];
  onJumpToParagraaf: (index: number) => void;
}) => {
  const openModal = useOpenModal();

  const paragrafen = paragraafLineages.map(({ paragrafen }) => ({
    data: paragrafen[0]!,
  }));

  return (
    <Body.ContentHeader
      metadataSwitch
      sticky
      button={useMemo(
        () => ({
          onClick: () => {
            openModal(JumpToParagraaf, {
              onClick: ({ index }) => onJumpToParagraaf(index),
              hoofdstukNummer,
              paragrafen,
            });
          },
          icon: RegularList,
          children: 'Alle paragrafen',
        }),
        [onJumpToParagraaf, paragrafen, openModal, hoofdstukNummer]
      )}
    >
      <Body.VigerendTitle date={date} />
    </Body.ContentHeader>
  );
};
